"use client";
 import { Helmet } from "react-helmet";
import { useEffect, useRef, useState } from "react";

/* ── scroll reveal hook ── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: v ? 1 : 0,
      transform: v ? "translateY(0)" : "translateY(32px)",
      transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
    }}>{children}</div>
  );
}

/* ── animated counter ── */
function useCounter(end, duration = 2000, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active]);
  return val;
}

/* ── typing code animation ── */
function TypingCode() {
  const lines = [
    { text: "// SoftCoders API Integration", color: "#64748b" },
    { text: "const api = new SoftCodersAPI({", color: "#e2e8f0" },
    { text: '  baseURL: "https://api.yourapp.com",', color: "#a78bfa" },
    { text: '  version: "v2",', color: "#a78bfa" },
    { text: '  auth: "Bearer <token>",', color: "#34d399" },
    { text: "});", color: "#e2e8f0" },
    { text: "", color: "" },
    { text: "// Fetch user data", color: "#64748b" },
    { text: "const { data } = await api", color: "#e2e8f0" },
    { text: '  .get("/users/me")', color: "#60a5fa" },
    { text: "  .withCache(300)", color: "#60a5fa" },
    { text: "  .retry(3);", color: "#60a5fa" },
    { text: "", color: "" },
    { text: "// ✓ 200 OK — 42ms", color: "#34d399" },
    { text: '// { id: "usr_abc", role: "admin" }', color: "#475569" },
  ];

  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (shown >= lines.length) return;
    const t = setTimeout(() => setShown(s => s + 1), 120 + Math.random() * 60);
    return () => clearTimeout(t);
  }, [shown]);

  return (
    <div style={{ fontFamily: "monospace", fontSize: 13, lineHeight: 1.9 }}>
      {lines.slice(0, shown).map((l, i) => (
        <div key={i} style={{ color: l.color || "transparent" }}>
          {l.text || "\u00a0"}
        </div>
      ))}
      {shown < lines.length && (
        <span style={{ display: "inline-block", width: 2, height: 16, background: "#a78bfa", verticalAlign: "middle", animation: "apiBlink 1s step-end infinite" }} />
      )}
    </div>
  );
}

/* ── canvas particle field ── */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const pts = Array.from({ length: 45 }, () => ({
      x: Math.random() * c.width, y: Math.random() * c.height,
      r: Math.random() * 1.2 + 0.4,
      vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
      o: Math.random() * 0.35 + 0.08,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      pts.forEach(p => {
        p.x = (p.x + p.vx + c.width) % c.width;
        p.y = (p.y + p.vy + c.height) % c.height;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99,102,241,${p.o})`; ctx.fill();
      });
      pts.forEach((a, i) => pts.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 100) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(99,102,241,${0.1 * (1 - d / 100)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />;
}

/* ── flow diagram ── */
function APIFlowDiagram() {
  const nodes = [
    { label: "Your App", icon: "📱", color: "#6366f1" },
    { label: "API Gateway", icon: "⚡", color: "#a78bfa" },
    { label: "Auth Layer", icon: "🔐", color: "#34d399" },
    { label: "Services", icon: "⚙️", color: "#60a5fa" },
    { label: "Database", icon: "🗄️", color: "#f59e0b" },
  ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % nodes.length), 1200);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
       

<Helmet>
  <title>
    API Integration Services Company in India | Custom API Development | SoftCoders
  </title>

  <meta
    name="description"
    content="SoftCoders provides API integration services including payment gateway integration, third-party API development, CRM integrations, ERP integrations, cloud APIs, and custom business automation solutions."
  />

  <meta
    name="keywords"
    content="
    API integration services,
    API development company,
    custom API integration,
    third party API integration,
    payment gateway integration,
    REST API development,
    API software solutions,
    ERP API integration,
    CRM API integration,
    cloud API integration,
    business automation APIs,
    SaaS API integration,
    API integration company india,
    secure API development,
    enterprise API solutions,
    mobile API integration,
    web API development,
    backend API services,
    software integration services,
    SoftCoders
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="SoftCoders" />
  <meta name="language" content="English" />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://softcoders.in/api-integration"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="API Integration Services Company | SoftCoders"
  />

  <meta
    property="og:description"
    content="SoftCoders offers secure API integration services including payment APIs, CRM integrations, ERP APIs, cloud integrations, and custom automation solutions."
  />

  <meta
    property="og:url"
    content="https://softcoders.in/api-integration"
  />

  <meta property="og:type" content="website" />

  <meta
    property="og:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* Twitter SEO */}
  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="API Integration Services | SoftCoders"
  />

  <meta
    name="twitter:description"
    content="Custom API integration solutions for ERP, CRM, payment gateways, cloud platforms, SaaS applications, and business automation systems."
  />

  <meta
    name="twitter:image"
    content="https://softcoders.in/og-image.jpg"
  />

  {/* GEO SEO */}
  <meta name="geo.region" content="IN-HR" />
  <meta name="geo.placename" content="Sirsa, Haryana" />

  {/* Mobile SEO */}
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  />

  {/* Theme */}
  <meta name="theme-color" content="#0a0d1f" />

  {/* Schema Markup */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "SoftwareCompany",
        "name": "SoftCoders",
        "url": "https://softcoders.in",
        "description": "SoftCoders provides API integration services including payment gateway APIs, CRM integrations, ERP APIs, cloud integrations, and custom software connectivity solutions.",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Sirsa",
          "addressRegion": "Haryana",
          "addressCountry": "India"
        },
        "serviceType": [
          "API Integration Services",
          "Custom API Development",
          "Third Party API Integration",
          "Payment Gateway Integration",
          "ERP API Solutions",
          "CRM API Integration"
        ]
      }
    `}
  </script>
</Helmet>

      {nodes.map((n, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: active === i ? n.color + "33" : "rgba(15,20,50,.8)",
            border: `1.5px solid ${active === i ? n.color : n.color + "44"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, transition: "all .4s ease",
            boxShadow: active === i ? `0 0 16px ${n.color}66` : "none",
          }}>{n.icon}</div>
          <div style={{ flex: 1, height: 6, borderRadius: 99, background: "rgba(255,255,255,.06)", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 99,
              background: `linear-gradient(90deg,${n.color},${n.color}88)`,
              width: active >= i ? "100%" : "0%",
              transition: "width .6s ease",
            }} />
          </div>
          <span style={{ fontSize: 12, color: active === i ? "#fff" : "#475569", fontWeight: 600, minWidth: 72, transition: "color .3s" }}>{n.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════ */
export default function APIIntegrationPage() {
  const statsRef = useRef(null);
  const [statsOn, setStatsOn] = useState(false);
  const c1 = useCounter(99, 1800, statsOn);
  const c2 = useCounter(500, 2000, statsOn);
  const c3 = useCounter(42, 1600, statsOn);
  const c4 = useCounter(150, 2200, statsOn);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsOn(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  /* inject styles */
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = `
      @keyframes apiBlink   { 0%,100%{opacity:1} 50%{opacity:0} }
      @keyframes apiFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
      @keyframes apiFloatR  { 0%,100%{transform:translateY(0) rotate(0deg)} 40%{transform:translateY(-18px) rotate(1.5deg)} }
      @keyframes apiFadeUp  { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
      @keyframes apiPing    { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.5);opacity:0} }
      @keyframes apiPulse   { 0%,100%{opacity:1} 50%{opacity:.4} }
      @keyframes apiShimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
      @keyframes apiMorph   { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
      @keyframes apiSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
      @keyframes apiData    { 0%{top:-10px;opacity:0} 100%{top:110%;opacity:0} 25%,75%{opacity:1} }

      .api-shimmer { background:linear-gradient(90deg,#6366f1,#a78bfa,#60a5fa,#6366f1);background-size:250% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:apiShimmer 3s linear infinite }
      .api-morph   { background:linear-gradient(135deg,#4f46e5,#7c3aed,#2563eb,#4f46e5)!important;background-size:300% 300%!important;animation:apiMorph 4s ease infinite!important }
      .api-float   { animation:apiFloat 7s ease-in-out infinite }
      .api-floatr  { animation:apiFloatR 9s ease-in-out infinite }
      .api-h1      { animation:apiFadeUp .8s ease .1s forwards;opacity:0 }
      .api-h2      { animation:apiFadeUp .8s ease .25s forwards;opacity:0 }
      .api-h3      { animation:apiFadeUp .8s ease .4s forwards;opacity:0 }
      .api-p       { animation:apiFadeUp .8s ease .55s forwards;opacity:0 }
      .api-btns    { animation:apiFadeUp .8s ease .7s forwards;opacity:0 }

      /* service card hover */
      .api-svc { transition:all .4s cubic-bezier(.34,1.56,.64,1); border:1px solid rgba(99,102,241,.15); background:rgba(10,14,40,.6); }
      .api-svc:hover { transform:translateY(-10px) scale(1.02); border-color:rgba(99,102,241,.5)!important; background:rgba(15,20,55,.8)!important; }
      .api-svc:hover .api-svc-icon { transform:scale(1.15) rotate(-5deg); }
      .api-svc-icon { transition:transform .3s ease; }

      /* endpoint row */
      .api-ep { transition:all .35s ease; cursor:default; }
      .api-ep:hover { background:rgba(99,102,241,.07)!important; border-color:rgba(99,102,241,.4)!important; transform:translateX(8px); }

      /* tech chip */
      .api-chip { transition:all .3s ease; }
      .api-chip:hover { background:rgba(99,102,241,.15)!important; border-color:rgba(99,102,241,.5)!important; transform:translateY(-5px); }

      /* why card */
      .api-why { transition:all .35s ease; cursor:default; }
      .api-why:hover { background:rgba(99,102,241,.08)!important; border-color:rgba(99,102,241,.4)!important; transform:translateY(-4px); }

      /* CTA btn */
      .api-cta-p { transition:all .4s ease; }
      .api-cta-p:hover { transform:scale(1.05); box-shadow:0 20px 60px rgba(79,70,229,.4)!important; }
      .api-cta-s { transition:all .3s ease; }
      .api-cta-s:hover { border-color:rgba(167,139,250,.45)!important; background:rgba(99,102,241,.06); color:#fff!important; }

      /* ── RESPONSIVE ── */

      /* Desktop base */
      .api-wrap       { max-width:1200px; margin:0 auto; }
      .api-hero       { padding:80px 64px 56px; min-height:100vh; }
      .api-hero-grid  { display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:center; }
      .api-section    { padding:100px 64px; }
      .api-section-sm { padding:72px 64px; }
      .api-svc-grid   { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
      .api-ep-grid    { display:grid; grid-template-columns:80px 120px 1fr auto; gap:16px; align-items:center; padding:20px 28px; }
      .api-tech-grid  { display:grid; grid-template-columns:repeat(9,1fr); gap:10px; }
      .api-why-grid   { display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center; }
      .api-why-cards  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
      .api-stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; }
      .api-cta-btns   { display:flex; flex-wrap:wrap; justify-content:center; gap:16px; }
      .api-trust      { display:flex; flex-wrap:wrap; justify-content:center; gap:36px; }
      .api-topbar     { display:flex; align-items:center; justify-content:space-between; }
      .api-topbar-right { display:flex; }
      .api-float-card1 { display:block; }
      .api-float-card2 { display:block; }

      /* ── TABLET ── */
      @media (max-width:1024px) {
        .api-hero       { padding:60px 40px 48px; min-height:auto; }
        .api-section    { padding:80px 40px; }
        .api-section-sm { padding:56px 40px; }
        .api-svc-grid   { grid-template-columns:repeat(2,1fr); }
        .api-ep-grid    { grid-template-columns:60px 100px 1fr; padding:16px 20px; }
        .api-ep-method-tag { display:none; }
        .api-tech-grid  { grid-template-columns:repeat(5,1fr); }
        .api-why-grid   { grid-template-columns:1fr; gap:48px; }
        .api-why-cards  { grid-template-columns:1fr 1fr; }
        .api-stats-grid { grid-template-columns:repeat(4,1fr); }
        .api-topbar-right { display:none; }
        .api-float-card1 { display:none; }
        .api-float-card2 { display:none; }
      }

      /* ── MOBILE ── */
      @media (max-width:768px) {
        .api-hero       { padding:28px 20px 36px; }
        .api-hero-grid  { grid-template-columns:1fr!important; gap:0!important; }
        .api-section    { padding:56px 20px; }
        .api-section-sm { padding:40px 20px; }
        .api-svc-grid   { grid-template-columns:1fr; gap:16px; }
        .api-ep-grid    { grid-template-columns:52px 90px 1fr; gap:10px; padding:14px 16px; font-size:12px; }
        .api-tech-grid  { grid-template-columns:repeat(3,1fr); gap:8px; }
        .api-why-grid   { grid-template-columns:1fr; gap:32px; }
        .api-why-cards  { grid-template-columns:1fr; }
        .api-stats-grid { grid-template-columns:repeat(2,1fr); }
        .api-topbar-right { display:none; }
        .api-float-card1 { display:none; }
        .api-float-card2 { display:none; }
        .api-cta-btns   { flex-direction:column; align-items:stretch; }
        .api-cta-btns button { width:100%!important; justify-content:center!important; }
        .api-trust      { flex-direction:column; align-items:flex-start; gap:16px; padding:0 8px; }
        .api-hero-pills { display:none!important; }
        .api-svc:hover  { transform:none!important; }
        .api-ep:hover   { transform:none!important; }
      }

      /* ── SMALL MOBILE ── */
      @media (max-width:480px) {
        .api-stats-grid { grid-template-columns:repeat(2,1fr); }
        .api-tech-grid  { grid-template-columns:repeat(3,1fr); }
        .api-why-cards  { grid-template-columns:1fr; }
      }
    `;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const services = [
    { icon:"🔗", title:"REST API Development", desc:"Clean, documented RESTful APIs built with OpenAPI spec, versioning, rate limiting, and auto-generated SDKs.", color:"#6366f1" },
    { icon:"◈", title:"GraphQL APIs", desc:"Flexible GraphQL schemas with subscriptions, batching, and DataLoader for N+1 query prevention.", color:"#a78bfa" },
    { icon:"🔄", title:"Webhook Systems", desc:"Reliable webhook delivery with retry queues, event logs, signature verification, and developer dashboards.", color:"#60a5fa" },
    { icon:"🔐", title:"OAuth & Auth Flows", desc:"OAuth 2.0, JWT, API keys, and PKCE flows. Secure token management with refresh, revocation, and scopes.", color:"#34d399" },
    { icon:"🌐", title:"Third-Party Integrations", desc:"Stripe, Twilio, SendGrid, Slack, HubSpot, Salesforce and 100+ platform integrations built and maintained.", color:"#f59e0b" },
    { icon:"📡", title:"Real-Time APIs", desc:"WebSocket and Server-Sent Events for live dashboards, chat, notifications, and collaborative features.", color:"#ec4899" },
  ];

  const endpoints = [
    { method:"GET",  color:"#34d399", path:"/v2/users/{id}",       desc:"Fetch user profile with relations",  ms:"38ms"  },
    { method:"POST", color:"#60a5fa", path:"/v2/payments/charge",   desc:"Process payment via Stripe",        ms:"120ms" },
    { method:"PUT",  color:"#f59e0b", path:"/v2/tenants/{id}/plan", desc:"Upgrade subscription plan",         ms:"54ms"  },
    { method:"DEL",  color:"#f87171", path:"/v2/webhooks/{id}",     desc:"Remove webhook endpoint",           ms:"29ms"  },
    { method:"POST", color:"#60a5fa", path:"/v2/auth/token",        desc:"Issue OAuth access token",          ms:"61ms"  },
    { method:"GET",  color:"#34d399", path:"/v2/analytics/summary", desc:"Aggregated usage metrics",          ms:"87ms"  },
  ];

  const techs = [
    {l:"Node.js",c:"#68a063"},{l:"Express",c:"#ffffff"},{l:"FastAPI",c:"#009688"},
    {l:"GraphQL",c:"#e535ab"},{l:"Redis",c:"#dc382d"},{l:"PostgreSQL",c:"#336791"},
    {l:"Kafka",c:"#231f20"},{l:"Docker",c:"#2496ed"},{l:"AWS",c:"#ff9900"},
  ];

  const whyPoints = [
    ["API-First Design","We design the contract before writing a single line of implementation code."],
    ["Auto-Generated Docs","OpenAPI / Swagger docs generated from code — always in sync."],
    ["SDK Generation","Client libraries for JS, Python, Go auto-built from your API spec."],
    ["Zero Downtime Deploys","Blue/green deployments so upgrades never break production."],
    ["Rate Limiting & DDoS","Tiered rate limits, IP blocking, and WAF protection built-in."],
    ["End-to-End Testing","Contract tests, load tests, and chaos engineering before go-live."],
  ];

  return (
    <div style={{ background:"#060818", color:"#fff", overflow:"hidden", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

      {/* ══ HERO ══ */}
      <section className="api-hero" style={{ position:"relative" }}>
        <ParticleField />

        {/* bg glows */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:0 }}>
          <div className="api-floatr" style={{ position:"absolute", top:"-5%", right:"10%", width:600, height:600, borderRadius:"50%", opacity:.18, background:"radial-gradient(circle,#4f46e5,transparent 70%)" }} />
          <div className="api-float"  style={{ position:"absolute", bottom:"-5%", left:"5%",  width:400, height:400, borderRadius:"50%", opacity:.12, background:"radial-gradient(circle,#7c3aed,transparent 70%)" }} />
          {/* spinning orbit ring */}
          <div style={{ position:"absolute", top:"50%", right:"2%", width:340, height:340, borderRadius:"50%", border:"1px solid rgba(99,102,241,.12)", transform:"translate(0,-50%)", animation:"apiSpin 30s linear infinite" }} />
          <div style={{ position:"absolute", top:"50%", right:"2%", width:260, height:260, borderRadius:"50%", border:"1px solid rgba(99,102,241,.08)", transform:"translate(calc(40px),-50%)", animation:"apiSpin 20s linear infinite reverse" }} />
        </div>

        {/* grid overlay */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:0, opacity:.03, backgroundImage:"linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)", backgroundSize:"72px 72px" }} />

        <div className="api-wrap" style={{ position:"relative", zIndex:10 }}>

          {/* topbar */}
          <div className="api-topbar" style={{ marginBottom:48 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ position:"relative" }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:"#6366f1", animation:"apiPulse 2s infinite" }} />
                <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"#6366f1", animation:"apiPing 1.5s ease infinite" }} />
              </div>
              <span style={{ color:"#818cf8", fontSize:13, letterSpacing:".2em", textTransform:"uppercase", fontWeight:500 }}>SoftCoders / API Services</span>
            </div>
            <div className="api-topbar-right" style={{ alignItems:"center", gap:8, fontSize:11, color:"rgba(255,255,255,.25)", letterSpacing:".15em", textTransform:"uppercase" }}>
              <span>REST · GraphQL · WebSockets</span>
              <span style={{ width:28, height:1, background:"rgba(255,255,255,.15)", display:"inline-block" }} />
              <span>500+ APIs Integrated</span>
            </div>
          </div>

          {/* two-col grid */}
          <div className="api-hero-grid">

            {/* LEFT */}
            <div>
              {/* eyebrow */}
              <div style={{ display:"inline-flex", alignItems:"center", gap:8, border:"1px solid rgba(99,102,241,.35)", background:"rgba(99,102,241,.1)", padding:"7px 16px", borderRadius:999, fontSize:12, color:"#a5b4fc", marginBottom:24, backdropFilter:"blur(10px)", animation:"apiFadeUp .7s ease forwards" }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#6366f1", animation:"apiPulse 2s infinite" }} />
                Enterprise-Grade API Integration
              </div>

              <h1 style={{ fontSize:"clamp(36px,5vw,72px)", fontWeight:900, lineHeight:.95, letterSpacing:"-2px" }}>
                <span className="api-h1" style={{ display:"block" }}>Connect</span>
                <span className="api-h2" style={{ display:"block" }}>Everything.</span>
                <span className="api-h3 api-shimmer" style={{ display:"block" }}>Automate.</span>
              </h1>

              <p className="api-p" style={{ marginTop:24, color:"#94a3b8", fontSize:17, lineHeight:1.8, maxWidth:480 }}>
                SoftCoders builds, integrates, and maintains high-performance APIs that connect your stack, automate workflows, and power real-time features at any scale.
              </p>

              {/* feature pills */}
              <div className="api-hero-pills" style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:20 }}>
                {["REST APIs","GraphQL","Webhooks","OAuth 2.0","Real-Time","SDK Generation"].map((p,i) => (
                  <span key={i} style={{
                    padding:"5px 13px", borderRadius:999, fontSize:12, fontWeight:500,
                    border:"1px solid rgba(99,102,241,.3)", color:"#a5b4fc",
                    background:"rgba(99,102,241,.08)", backdropFilter:"blur(8px)",
                    animation:`apiFadeUp .6s ease ${.7 + i*.07}s forwards`, opacity:0,
                  }}>{p}</span>
                ))}
              </div>

              <div className="api-btns" style={{ display:"flex", flexWrap:"wrap", gap:14, marginTop:32 }}>
                <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="api-morph api-cta-p" style={{ padding:"14px 28px", borderRadius:14, fontWeight:700, fontSize:16, border:"none", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
                  Start Integration Project
                  <span>→</span>
                </a>

                <a
    href="/contact" className="api-cta-s" style={{ padding:"14px 28px", borderRadius:14, border:"1px solid rgba(255,255,255,.15)", background:"transparent", color:"rgba(255,255,255,.8)", fontSize:16, fontWeight:500, cursor:"pointer" }}>
                  View API Docs
                </a>
              </div>
            </div>

            {/* RIGHT — terminal + flow + floating badges */}
            <div style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>

              {/* floating response time badge */}
              <div className="api-float-card1 api-float" style={{
                position:"absolute", top:0, left:-20, zIndex:20,
                background:"rgba(8,10,28,.92)", border:"1px solid rgba(99,102,241,.35)",
                borderRadius:14, padding:"12px 16px", backdropFilter:"blur(16px)",
                animation:"apiFadeUp .7s ease 1s forwards", opacity:0, minWidth:160,
              }}>
                <div style={{ color:"#64748b", fontSize:10, marginBottom:4 }}>Avg Response</div>
                <div style={{ fontSize:24, fontWeight:900, color:"#34d399" }}>42<span style={{ fontSize:13, color:"#64748b" }}>ms</span></div>
                <div style={{ color:"#64748b", fontSize:10 }}>p99 &lt; 120ms</div>
              </div>

              {/* floating uptime badge */}
              <div className="api-float-card2 api-floatr" style={{
                position:"absolute", bottom:20, right:-10, zIndex:20,
                background:"rgba(8,10,28,.92)", border:"1px solid rgba(52,211,153,.35)",
                borderRadius:14, padding:"12px 16px", backdropFilter:"blur(16px)",
                animation:"apiFadeUp .7s ease 1.2s forwards", opacity:0,
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:"#22c55e", animation:"apiPulse 2s infinite" }} />
                  <span style={{ color:"#22c55e", fontSize:11, fontWeight:700 }}>99.99% Uptime</span>
                </div>
                <div style={{ color:"#64748b", fontSize:10, marginTop:3 }}>500M+ requests/month</div>
              </div>

              {/* terminal card */}
              <div className="api-floatr" style={{
                width:"100%", maxWidth:480,
                background:"rgba(6,8,24,.95)", border:"1px solid rgba(99,102,241,.3)",
                borderRadius:20, overflow:"hidden",
                boxShadow:"0 32px 80px rgba(79,70,229,.2)",
                animation:"apiFadeUp .8s ease .5s forwards", opacity:0,
              }}>
                {/* terminal topbar */}
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,.06)", background:"rgba(10,14,40,.5)" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    {["#ef4444","#eab308","#22c55e"].map((c,i)=>(
                      <div key={i} style={{ width:10, height:10, borderRadius:"50%", background:c, opacity:.75, cursor:"pointer", transition:"opacity .2s" }}
                        onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".75"} />
                    ))}
                  </div>
                  <span style={{ fontSize:11, color:"#475569", fontFamily:"monospace", marginLeft:8 }}>api-integration.ts</span>
                  <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:99, background:"rgba(99,102,241,.2)", color:"#818cf8" }}>TypeScript</span>
                  </div>
                </div>
                {/* line numbers + code */}
                <div style={{ display:"flex", padding:"20px 0", minHeight:300 }}>
                  <div style={{ padding:"0 16px", fontFamily:"monospace", fontSize:12, color:"#2d3748", lineHeight:1.9, userSelect:"none", borderRight:"1px solid rgba(255,255,255,.04)", minWidth:40, textAlign:"right" }}>
                    {Array.from({length:15},(_,i)=><div key={i}>{i+1}</div>)}
                  </div>
                  <div style={{ padding:"0 20px", overflow:"hidden" }}>
                    <TypingCode />
                  </div>
                </div>
                {/* status bar */}
                <div style={{ display:"flex", alignItems:"center", gap:16, padding:"10px 20px", borderTop:"1px solid rgba(255,255,255,.04)", background:"rgba(10,14,40,.4)", fontSize:10, color:"#475569" }}>
                  <span style={{ color:"#34d399" }}>✓ Connected</span>
                  <span>UTF-8</span>
                  <span style={{ marginLeft:"auto", color:"#6366f1" }}>SoftCoders API v2</span>
                </div>
              </div>
            </div>
          </div>

          {/* stats strip */}
          <div ref={statsRef} className="api-stats-grid" style={{ marginTop:56, background:"rgba(255,255,255,.04)", borderRadius:18, overflow:"hidden", border:"1px solid rgba(255,255,255,.05)" }}>
            {[
              [`${c1}.99%`, "Uptime SLA"],
              [`${c2}+`,   "APIs Integrated"],
              [`${c3}ms`,  "Avg Response Time"],
              [`${c4}+`,   "Endpoints Delivered"],
            ].map(([v, l], i) => (
              <div key={i} style={{ padding:"24px 28px", transition:"background .4s, transform .3s", cursor:"default" }}
                onMouseEnter={e=>{e.currentTarget.style.background="rgba(99,102,241,.08)";e.currentTarget.style.transform="translateY(-3px)";}}
                onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.transform="none";}}>
                <div style={{ fontSize:26, fontWeight:900, background:"linear-gradient(135deg,#818cf8,#6366f1)", WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent" }}>{v}</div>
                <div style={{ color:"#64748b", fontSize:12, marginTop:4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══ */}
      <section className="api-section" style={{ borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div className="api-wrap">
          <Reveal>
            <p style={{ color:"#818cf8", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:12 }}>What We Build</p>
            <div style={{ display:"flex", alignItems:"flex-end", gap:24, marginBottom:56, flexWrap:"wrap" }}>
              <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, lineHeight:1.1 }}>
                API Integration <span className="api-shimmer">Services</span>
              </h2>
              <div style={{ flex:1, minWidth:120, height:1, background:"linear-gradient(90deg,rgba(99,102,241,.4),transparent)", marginBottom:8 }} />
            </div>
          </Reveal>

          <div className="api-svc-grid">
            {services.map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="api-svc" style={{ borderRadius:22, padding:28, cursor:"default", overflow:"hidden", position:"relative" }}>
                  <div style={{ position:"absolute", inset:0, background:`linear-gradient(135deg,${s.color}00,${s.color}08)`, opacity:0, transition:"opacity .4s" }} className="api-svc-hover-bg" />
                  {/* top color bar */}
                  <div style={{ height:3, background:`linear-gradient(90deg,${s.color},${s.color}44)`, borderRadius:99, marginBottom:22 }} />
                  <div className="api-svc-icon" style={{ fontSize:32, marginBottom:16 }}>{s.icon}</div>
                  <h3 style={{ fontSize:18, fontWeight:700, marginBottom:10 }}>{s.title}</h3>
                  <p style={{ color:"#64748b", fontSize:13, lineHeight:1.7 }}>{s.desc}</p>
                  <div style={{ marginTop:18, display:"flex", alignItems:"center", gap:6, fontSize:12, color:s.color, fontWeight:600 }}>
                    Learn More <span style={{ transition:"transform .3s" }}>→</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ LIVE ENDPOINTS ══ */}
      <section className="api-section" style={{ borderTop:"1px solid rgba(255,255,255,.05)", background:"rgba(255,255,255,.012)" }}>
        <div className="api-wrap">
          <Reveal style={{ display:"flex", flexWrap:"wrap", gap:24, alignItems:"flex-end", marginBottom:48 }}>
            <div>
              <p style={{ color:"#818cf8", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:10 }}>Sample Endpoints</p>
              <h2 style={{ fontSize:"clamp(24px,3.5vw,42px)", fontWeight:900 }}>
                Clean, Fast <span className="api-shimmer">API Design</span>
              </h2>
            </div>
            <div style={{ flex:1, minWidth:100, height:1, background:"linear-gradient(90deg,rgba(99,102,241,.3),transparent)", marginBottom:6 }} />
            <div style={{ padding:"6px 16px", borderRadius:99, border:"1px solid rgba(99,102,241,.3)", background:"rgba(99,102,241,.08)", color:"#818cf8", fontSize:12, fontWeight:600 }}>
              OpenAPI 3.0 Compliant
            </div>
          </Reveal>

          <div style={{ borderRadius:20, border:"1px solid rgba(99,102,241,.2)", overflow:"hidden" }}>
            {/* header row */}
            <div style={{ display:"grid", gridTemplateColumns:"80px 120px 1fr auto", gap:16, padding:"12px 28px", background:"rgba(10,14,40,.8)", borderBottom:"1px solid rgba(255,255,255,.05)", fontSize:11, color:"#475569", textTransform:"uppercase", letterSpacing:".1em" }}>
              <span>Method</span><span>Status</span><span>Endpoint</span><span>Latency</span>
            </div>
            {endpoints.map((ep, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="api-ep api-ep-grid" style={{ borderBottom: i < endpoints.length-1 ? "1px solid rgba(255,255,255,.04)" : "none", background:"rgba(6,8,24,.6)" }}>
                  <span style={{ padding:"4px 10px", borderRadius:6, fontSize:11, fontWeight:700, background:`${ep.color}18`, color:ep.color, textAlign:"center", fontFamily:"monospace" }}>{ep.method}</span>
                  <span style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#34d399" }}>
                    <span style={{ width:6, height:6, borderRadius:"50%", background:"#34d399", flexShrink:0 }} />200 OK
                  </span>
                  <span style={{ fontFamily:"monospace", fontSize:12, color:"#a5b4fc", wordBreak:"break-all" }}>{ep.path}</span>
                  <span className="api-ep-method-tag" style={{ fontFamily:"monospace", fontSize:11, color:"#64748b", whiteSpace:"nowrap" }}>{ep.ms}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ API FLOW ══ */}
      <section className="api-section" style={{ borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div className="api-wrap">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:64, alignItems:"center" }}>
            <Reveal>
              <p style={{ color:"#818cf8", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:12 }}>How It Works</p>
              <h2 style={{ fontSize:"clamp(24px,3.5vw,44px)", fontWeight:900, lineHeight:1.1, marginBottom:20 }}>
                Your Request.<br/><span className="api-shimmer">Our Pipeline.</span>
              </h2>
              <p style={{ color:"#64748b", lineHeight:1.8, marginBottom:32 }}>
                Every API request flows through our hardened gateway — authenticated, cached, rate-limited, and logged before hitting your business logic. Zero extra code on your end.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {[
                  ["Request Validation","Schema, auth, and rate limits checked at the edge."],
                  ["Smart Caching","Redis-backed cache with configurable TTL and invalidation."],
                  ["Observability","Distributed tracing, structured logs, and real-time alerts."],
                ].map(([t,d],i)=>(
                  <div key={i} className="api-why" style={{ display:"flex", gap:14, padding:"14px 16px", borderRadius:14, border:"1px solid rgba(99,102,241,.12)", background:"rgba(10,14,40,.5)" }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:"linear-gradient(135deg,#6366f1,#a78bfa)", flexShrink:0, marginTop:5 }} />
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>{t}</div>
                      <div style={{ color:"#64748b", fontSize:13, lineHeight:1.6 }}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div style={{ position:"relative" }}>
                <div className="api-floatr" style={{ position:"absolute", inset:-20, borderRadius:32, opacity:.15, filter:"blur(28px)", background:"linear-gradient(135deg,#4f46e5,#7c3aed)", pointerEvents:"none" }} />
                <div style={{ position:"relative", borderRadius:22, border:"1px solid rgba(99,102,241,.25)", background:"rgba(8,10,28,.95)", padding:28 }}>
                  {/* terminal header */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:24 }}>
                    <div style={{ display:"flex", gap:5 }}>
                      {["#ef4444","#eab308","#22c55e"].map((c,i)=>(
                        <div key={i} style={{ width:9, height:9, borderRadius:"50%", background:c, opacity:.7 }} />
                      ))}
                    </div>
                    <span style={{ fontSize:11, color:"#475569", fontFamily:"monospace", marginLeft:6 }}>request-pipeline.live</span>
                    <div style={{ marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:"#22c55e", animation:"apiPulse 2s infinite" }} />
                  </div>
                  <APIFlowDiagram />
                  <div style={{ marginTop:24, padding:"12px 16px", borderRadius:12, background:"rgba(99,102,241,.08)", border:"1px solid rgba(99,102,241,.2)", fontFamily:"monospace", fontSize:11, color:"#818cf8" }}>
                    <span style={{ color:"#34d399" }}>✓</span> Pipeline healthy · 42ms avg · 0 errors
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK ══ */}
      <section className="api-section-sm" style={{ borderTop:"1px solid rgba(255,255,255,.05)", background:"rgba(255,255,255,.012)" }}>
        <div className="api-wrap">
          <Reveal>
            <p style={{ textAlign:"center", color:"#334155", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:40 }}>Technology Stack</p>
          </Reveal>
          <div className="api-tech-grid">
            {techs.map((t, i) => (
              <Reveal key={i} delay={i * 40}>
                <div className="api-chip" style={{ padding:"12px 8px", borderRadius:14, background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.06)", textAlign:"center", cursor:"default" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:t.c, margin:"0 auto 8px", boxShadow:`0 0 6px ${t.c}88` }} />
                  <div style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{t.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY SOFTCODERS ══ */}
      <section className="api-section" style={{ borderTop:"1px solid rgba(255,255,255,.05)" }}>
        <div className="api-wrap">
          <div className="api-why-grid">
            <Reveal>
              <p style={{ color:"#818cf8", fontSize:11, letterSpacing:".3em", textTransform:"uppercase", fontWeight:600, marginBottom:14 }}>Why SoftCoders</p>
              <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:900, lineHeight:1.1 }}>
                APIs That Are<br/><span className="api-shimmer">Built to Last.</span>
              </h2>
              <p style={{ color:"#64748b", marginTop:20, lineHeight:1.8, fontSize:16 }}>
                We've integrated over 500 third-party platforms and built custom APIs serving hundreds of millions of requests per month. We don't just write endpoints — we architect systems.
              </p>
              <p style={{ color:"#64748b", marginTop:14, lineHeight:1.8 }}>
                From initial design through post-launch monitoring, our API team is your long-term integration partner.
              </p>
            </Reveal>

            <Reveal delay={150}>
              <div className="api-why-cards">
                {whyPoints.map(([title, sub], i) => (
                  <div key={i} className="api-why" style={{ padding:"18px 20px", borderRadius:16, background:"rgba(10,14,40,.6)", border:"1px solid rgba(99,102,241,.12)" }}>
                    <div style={{ width:24, height:2, background:"linear-gradient(90deg,#6366f1,#a78bfa)", marginBottom:12 }} />
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:5 }}>{title}</div>
                    <div style={{ color:"#475569", fontSize:12, lineHeight:1.5 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      
    </div>
  );
}